
import React, { useState, useRef, useEffect, useId, useCallback } from 'react';
import Image, { ImageProps } from '@/components/AppImage/AppImage';
import { transformImageUrl } from '@/utils/image.util';
import { warn } from '@/utils/logger';
import './CustomImage.css';

interface CustomImageProps extends Omit<ImageProps, 'src' | 'onError' | 'onLoad'> {
  src: string | null | undefined;
  /** Optional list of URLs to try in order when src fails (e.g. next images in a carousel). Tried before fallbackSrc. */
  fallbackSources?: string[];
  fallbackSrc?: string;
  lazy?: boolean;
  onError?: (error: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

/**
 * Custom Image component that transforms S3 bucket URLs, provides fallback, and supports lazy loading
 */
export default function CustomImage({ 
  src, 
  fallbackSources,
  fallbackSrc = '/images/default_post.png',
  alt = '',
  lazy = true,
  onError: externalOnError,
  onLoad: externalOnLoad,
  style,
  width,
  height,
  fill,
  ...restProps 
}: CustomImageProps) {
  // State management
  const [fallbackLevel, setFallbackLevel] = useState(0);
  const [isVisible, setIsVisible] = useState(!lazy);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const imgRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const key = useId();

  // Fallback chain: primary src, then any alternate URLs (e.g. next carousel images), then static fallbacks
  const transformedFallbacks = (fallbackSources ?? []).map((u) => transformImageUrl(u)).filter(Boolean);
  const fallbackChain = [
    transformImageUrl(src),
    ...transformedFallbacks,
    fallbackSrc,
    '/images/default_post.png',
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5JbWFnZTwvdGV4dD48L3N2Zz4='
  ].filter(Boolean);

  // When the caller swaps in a better src (e.g. post-hero enrichment), restart the chain.
  // Otherwise we can remain stuck on DEFAULT_POST_IMAGE after an earlier failed API thumb.
  const chainIdentity = `${transformImageUrl(src) ?? ''}|${(fallbackSources ?? []).join(',')}`;
  useEffect(() => {
    setFallbackLevel(0);
    setHasLoaded(false);
    setImageError(false);
  }, [chainIdentity]);

  // Get current image source
  const currentSrc = fallbackChain[Math.min(fallbackLevel, fallbackChain.length - 1)];

  // Cached images often fire `onLoad` before the chain-reset effect above. Re-check
  // `complete` after paint so we don't leave opacity/fade stuck in the loading state.
  useEffect(() => {
    const img = imgRef.current?.querySelector('img');
    if (img?.complete && img.naturalWidth > 0) {
      setHasLoaded(true);
      setImageError(false);
    }
  }, [chainIdentity, currentSrc, fallbackLevel]);

  // Resolve prop conflicts - ensure only compatible props are passed
  const imageProps = (() => {
    // Create a properly typed props object
    const allProps = { ...restProps, width, height, fill, style };
    
    // Handle dimension props - prioritize fill over width/height
    if (fill) {
      // When using fill, remove width/height to avoid conflict
      const { width: _unused, height: _unused2, ...fillProps } = allProps;
      return { ...fillProps, fill: true };
    } else {
      // Use provided dimensions or defaults
      const { fill: _unused, ...dimensionProps } = allProps;
      return {
        ...dimensionProps,
        width: width || 100,
        height: height || 100
      };
    }
  })();

  // Enhanced error handler with fallback chain
  const handleImageError = useCallback((error: React.SyntheticEvent<HTMLImageElement, Event>) => {
    warn(`Image failed to load: ${currentSrc}`);
    
    // Try next fallback if available
    if (fallbackLevel < fallbackChain.length - 1) {
      setFallbackLevel(prev => prev + 1);
      setImageError(false);
    } else {
      setImageError(true);
    }
    
    setIsLoading(false);
    externalOnError?.(error);
  }, [currentSrc, fallbackLevel, fallbackChain.length, externalOnError]);

  // Enhanced load handler
  const handleImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setHasLoaded(true);
    setIsLoading(false);
    setImageError(false);
    externalOnLoad?.(event);
  }, [externalOnLoad]);

  // Enhanced Intersection Observer with error handling and browser compatibility
  useEffect(() => {
    if (!lazy || hasLoaded || isVisible) return;

    // Check if IntersectionObserver is supported
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // Fallback for older browsers - load immediately
      setIsVisible(true);
      return;
    }

    try {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              setIsLoading(true);
            }
          });
        },
        {
          rootMargin: '50px',
          threshold: 0.1
        }
      );

      if (imgRef.current) {
        observer.observe(imgRef.current);
        observerRef.current = observer;
      }

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
      };
    } catch (error) {
      warn('Failed to initialize IntersectionObserver:', error);
      // Fallback - load immediately
      setIsVisible(true);
    }
  }, [lazy, hasLoaded, isVisible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Early return for invalid src and no fallbacks
  if (!currentSrc) {
    return (
      <div 
        ref={imgRef} 
        style={{ 
          backgroundColor: '#f0f0f0', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '12px',
          ...style 
        }}
      >
        Image N/A
      </div>
    );
  }

  return (
    <div 
      ref={imgRef} 
      style={{ 
        backgroundColor: isLoading ? '#f0f0f0' : 'transparent',
        position: fill ? 'relative' : undefined,
        height: fill ? '100%' : undefined,
        width: fill ? '100%' : undefined,
        minHeight: 0,
        ...style 
      }}
    >
      {isVisible ? (
        <>
          <Image
            key={`${key}-${fallbackLevel}-${currentSrc}`}
            src={currentSrc}
            alt={alt}
            {...imageProps}
            onError={handleImageError}
            onLoad={handleImageLoad}
            priority={!lazy}
            style={{
              ...(imageProps.style || {}),
              opacity: hasLoaded ? 1 : 0.85,
              transition: 'opacity 0.2s ease-in-out',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
          {imageError && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                color: '#666',
                fontSize: '12px',
                zIndex: 1,
                border: '1px solid #e0e0e0'
              }}
            >
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📷</div>
              <div>Image unavailable</div>
            </div>
          )}
        </>
      ) : (
        // Enhanced placeholder with progressive loading effect
        <div
          style={{
            width: !fill ? (width || 100) : '100%',
            height: !fill ? (height || 100) : '100%',
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '12px',
            minHeight: fill ? '200px' : undefined,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Animated shimmer effect */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              animation: 'globethrivers-custom-image-shimmer 2s infinite',
              transform: 'translateX(-100%)'
            }}
          />
          <span style={{ position: 'relative', zIndex: 1 }}>Loading...</span>
        </div>
      )}
    </div>
  );
} 